/* eslint-disable @typescript-eslint/no-explicit-any */
// Base Client
import { getApiToken } from "../helpers";
import { getBffRequest } from "./base/api";

// Protos
import { ClaimsBFFControllerClient } from "./protos/Claims-bffServiceClientPb";
import {
	DownloadFileRequest,
	DownloadFileResponse,
	DownloadFileChunkResponse,
	File as CoreFile
} from "./protos/messages/file.message_pb";
import { RpcError } from "grpc-web";

export const downloadDoc = async (file_id: string): Promise<DownloadFileResponse> => {
	const token: string = getApiToken();

	const request: DownloadFileRequest = new DownloadFileRequest();
	request.setFileId(file_id);

	const client: ClaimsBFFControllerClient = getBffRequest(token);
	return client
		.downloadDoc(request, {})
		.then((response: DownloadFileResponse) => response)
		.catch((err) => {
			console.error("Regular download error:", err);
			throw err;
		});
};

/**
 * Download a file using streaming API (optimized for all file sizes)
 * Falls back to regular download if streaming fails
 * @param file_id The file ID to download
 * @param onProgress Optional callback for progress updates (percentage: 0-100)
 * @returns Promise with the complete file response
 */
export const downloadDocStream = async (
	file_id: string,
	onProgress?: (progress: number) => void
): Promise<DownloadFileResponse> => {
	const token: string = getApiToken();

	const request: DownloadFileRequest = new DownloadFileRequest();
	request.setFileId(file_id);

	const client: ClaimsBFFControllerClient = getBffRequest(token);

	console.log(`[downloadDocStream] Starting download for file ID: ${file_id}`);

	// Always try streaming first (optimized for all file sizes)
	try {
		return await new Promise((resolve, reject) => {
			const chunks: Uint8Array[] = [];
			let totalSize = 0;
			let receivedBytes = 0;
			let fileName = "";
			let mimeType = "";
			let streamErrorOccurred = false;
			let chunkCount = 0;

			try {
				const stream = client.downloadDocStream(request, {});

				stream.on("data", (chunk: DownloadFileChunkResponse) => {
					chunkCount++;
					const chunkData = chunk.getChunkData_asU8();
					chunks.push(chunkData);
					receivedBytes += chunkData.length;

					// Get metadata from first chunk
					if (chunks.length === 1) {
						totalSize = chunk.getTotalSize();
						fileName = chunk.getFileName();
						mimeType = chunk.getMimeType();
						console.log(
							`[downloadDocStream] File metadata - Name: ${fileName}, Size: ${totalSize} bytes, Type: ${mimeType}`
						);
					}

					// Update progress
					if (onProgress && totalSize > 0) {
						const progress = Math.round((receivedBytes / totalSize) * 100);
						console.log(`[downloadDocStream] Progress: ${progress}%`);
						onProgress(progress);
					}

					// Log progress every 3 chunks or on last chunk
					if (chunkCount % 3 === 0 || chunk.getIsLastChunk()) {
						console.log(
							`[downloadDocStream] Progress: ${receivedBytes}/${totalSize} bytes (${Math.round((receivedBytes / totalSize) * 100)}%) - Chunk ${chunkCount}`
						);
					}

					// Check if this is the last chunk
					if (chunk.getIsLastChunk()) {
						console.log(
							`[downloadDocStream] Received final chunk. Total chunks: ${chunkCount}, Total bytes: ${receivedBytes}`
						);

						// Combine all chunks into a single Uint8Array
						const totalLength = chunks.reduce((total, arr) => total + arr.length, 0);
						const completeFile = new Uint8Array(totalLength);
						let position = 0;

						for (const chunkData of chunks) {
							completeFile.set(chunkData, position);
							position += chunkData.length;
						}

						// Create the file object
						const file = new CoreFile();
						file.setFileName(fileName);
						file.setMimeType(mimeType);
						file.setContent(completeFile);

						// Create the response
						const response = new DownloadFileResponse();
						response.setFile(file);

						console.log(`[downloadDocStream] Successfully assembled file: ${fileName} (${completeFile.length} bytes)`);
						resolve(response);
					}
				});

				stream.on("error", (err: RpcError) => {
					streamErrorOccurred = true;
					console.error(`[downloadDocStream] Stream error (code ${err.code}):`, err.message);
					console.error(`[downloadDocStream] Full error details:`, err);

					// Check for specific error codes that indicate we should fallback
					if (
						err.code === 13 || // OutOfRange (message too large)
						err.code === 7 || // PermissionDenied (might work with regular download)
						err.code === 16 // Unauthenticated (auth interceptor should handle this)
					) {
						console.log(
							`[downloadDocStream] Error code ${err.code} detected, attempting fallback to regular download...`
						);
						reject(new Error("FALLBACK_TO_REGULAR"));
					} else {
						console.error(`[downloadDocStream] Unhandled error code ${err.code}, not attempting fallback`);
						reject(err);
					}
				});

				stream.on("end", () => {
					console.log(
						`[downloadDocStream] Stream ended. Chunks received: ${chunkCount}, Error occurred: ${streamErrorOccurred}`
					);
					// Stream ended, but we should have already resolved in the last chunk
					if (chunks.length === 0 && !streamErrorOccurred) {
						console.error("[downloadDocStream] Stream ended without receiving any data");
						reject(new Error("Stream ended without receiving any data"));
					}
				});
			} catch (err) {
				console.error("[downloadDocStream] Error starting stream:", err);
				reject(new Error("FALLBACK_TO_REGULAR"));
			}
		});
	} catch (error: unknown) {
		// If streaming failed and we should fallback, try regular download
		if (error instanceof Error && error.message === "FALLBACK_TO_REGULAR") {
			console.log(`[downloadDocStream] Falling back to regular download method for file ID: ${file_id}`);
			try {
				const response = await downloadDoc(file_id);
				if (onProgress) {
					onProgress(100); // Set progress to 100% since regular download doesn't provide progress
				}
				console.log(`[downloadDocStream] Fallback download successful for file ID: ${file_id}`);
				return response;
			} catch (fallbackError) {
				console.error(`[downloadDocStream] Fallback download also failed for file ID: ${file_id}:`, fallbackError);
				throw fallbackError;
			}
		} else {
			// Re-throw the original error if it's not a fallback case
			console.error(`[downloadDocStream] Non-fallback error for file ID: ${file_id}:`, error);
			throw error;
		}
	}
};

/**
 * Read a file as a Uint8Array with progress tracking
 * @param file The file to read
 * @param onProgress Optional callback for progress updates
 * @returns Promise with the file contents as Uint8Array
 */
export const readFileWithProgress = async (
	file: File,
	onProgress?: (progress: number) => void
): Promise<Uint8Array> => {
	return new Promise((resolve, reject) => {
		// For small files (< 10MB), just read the whole file at once
		if (file.size < 10 * 1024 * 1024) {
			const reader = new FileReader();
			reader.onload = () => {
				const arrayBuffer = reader.result as ArrayBuffer;
				resolve(new Uint8Array(arrayBuffer));
				if (onProgress) onProgress(100);
			};
			reader.onerror = () => reject(reader.error);
			reader.readAsArrayBuffer(file);
			return;
		}

		// For larger files, read in chunks to show progress
		const chunkSize = 5 * 1024 * 1024; // 5MB chunks
		const chunks: Uint8Array[] = [];
		let offset = 0;
		let bytesRead = 0;

		const readNextChunk = () => {
			const chunk = file.slice(offset, offset + chunkSize);
			const reader = new FileReader();

			reader.onload = (e) => {
				const arrayBuffer = reader.result as ArrayBuffer;
				chunks.push(new Uint8Array(arrayBuffer));
				bytesRead += arrayBuffer.byteLength;

				// Update progress
				if (onProgress) {
					onProgress(Math.round((bytesRead / file.size) * 100));
				}

				offset += chunkSize;

				if (offset < file.size) {
					// Read the next chunk
					readNextChunk();
				} else {
					// Combine all chunks
					const totalLength = chunks.reduce((total, arr) => total + arr.length, 0);
					const result = new Uint8Array(totalLength);
					let position = 0;

					for (const chunk of chunks) {
						result.set(chunk, position);
						position += chunk.length;
					}

					resolve(result);
				}
			};

			reader.onerror = () => reject(reader.error);
			reader.readAsArrayBuffer(chunk);
		};

		// Start reading
		readNextChunk();
	});
};

/**
 * Create a CoreFile object from a File with progress tracking
 * @param file The file to convert
 * @param onProgress Optional callback for progress updates
 * @returns Promise with the CoreFile object
 */
export const createCoreFile = async (file: File, onProgress?: (progress: number) => void): Promise<CoreFile> => {
	const bytes = await readFileWithProgress(file, onProgress);
	const coreFile = new CoreFile();
	coreFile.setFileName(file.name);
	coreFile.setMimeType(file.type);
	coreFile.setContent(bytes);
	return coreFile;
};
